import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Plus, DollarSign } from "@/components/icons";

import {
  fetchOrganizations, fetchOrganization, fetchPricingProfiles,
  createPricingProfile, updatePricingProfile, disablePricingProfile,
  fetchPricingDrivers, enrollPricingDriver, unenrollPricingDriver,
  fetchPricingGroups, createPricingGroup, deletePricingGroup,
  fetchPricingGroupMembers, addPricingGroupMember, removePricingGroupMember,
  fetchPricingAssignments, createPricingAssignment, disablePricingAssignment,
  type Organization, type PricingProfile, type PricingGroup, type PricingAssignment,
  type PricingAudienceType,
} from "@/lib/api";
import { PricingForm } from "@/components/PricingForm";
import {
  type PricingFormValues, emptyPricingForm, pricebookToValues, valuesToPricebook,
  validatePricingForm, summarizePricebook, isPricebookFree,
} from "@/lib/pricingForm";

function apiErr(e: any): string {
  const b = e?.body as any;
  return b?.message || b?.error || e?.message || "Request failed";
}

function OrgSelector({ orgs, selected, onSelect }: {
  orgs: Organization[]; selected: number | null; onSelect: (id: number) => void;
}) {
  if (orgs.length === 0)
    return <div className="text-sm opacity-50">No organizations found. Create one first.</div>;
  return (
    <SearchableSelect
      className="w-64"
      options={orgs.map((o) => ({ value: String(o.id), label: o.name }))}
      value={selected != null ? String(selected) : null}
      onValueChange={(v) => onSelect(Number(v))}
      placeholder="Select organization..."
      searchPlaceholder="Search organizations..."
      emptyText="No organizations match."
    />
  );
}

/* ── Pricing Profile create/edit modal — uses the SHARED PricingForm. ── */
function ProfileFormModal({ open, onClose, orgId, editing, onSaved }: {
  open: boolean; onClose: () => void; orgId: number;
  editing: PricingProfile | null; onSaved: () => void;
}) {
  const [name, setName] = useState("");
  const [pricing, setPricing] = useState<PricingFormValues>(emptyPricingForm());

  useEffect(() => {
    if (!open) return;
    setName(editing ? editing.name : "");
    setPricing(editing ? pricebookToValues(editing.pricebook) : emptyPricingForm());
  }, [open, editing]);

  const mutation = useMutation({
    mutationFn: () => {
      const pricebook = valuesToPricebook(pricing);
      if (editing)
        return updatePricingProfile(orgId, editing.id, { name: name.trim(), pricebook, expected_revision: editing.revision });
      return createPricingProfile(orgId, { name: name.trim(), pricebook });
    },
    onSuccess: () => { toast.success(editing ? "Pricing profile updated" : "Pricing profile created"); onSaved(); onClose(); },
    onError: (e: any) => toast.error(apiErr(e)),
  });

  const submit = () => {
    if (!name.trim()) { toast.error("Name is required."); return; }
    const err = validatePricingForm(pricing);
    if (err) { toast.error(err); return; }
    mutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Pricing Profile" : "Create Pricing Profile"}</DialogTitle>
          <DialogDescription>
            A reusable rate you can assign to a driver or a pricing driver group. It applies at every
            charger in this organization. Other drivers and guests keep the charger’s default rate.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Name</Label>
            <Input className="mt-1" placeholder="e.g. Employee Discount"
              value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <PricingForm value={pricing} onChange={setPricing} idPrefix="profile" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={mutation.isPending}>
            {mutation.isPending ? "Saving…" : editing ? "Save changes" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Assignment modal: profile → audience (individual driver | pricing group). NO charger. ── */
function AssignmentFormModal({
  open, onClose, orgId, profiles, groups, drivers, userName, onSaved,
}: {
  open: boolean; onClose: () => void; orgId: number;
  profiles: PricingProfile[]; groups: PricingGroup[]; drivers: number[];
  userName: Map<number, string>; onSaved: () => void;
}) {
  const [profileId, setProfileId] = useState("");
  const [audienceType, setAudienceType] = useState<PricingAudienceType>("pricing_driver_group");
  const [audienceId, setAudienceId] = useState("");

  useEffect(() => {
    if (!open) return;
    setProfileId(""); setAudienceType("pricing_driver_group"); setAudienceId("");
  }, [open]);

  const mutation = useMutation({
    mutationFn: () => createPricingAssignment(orgId, {
      pricing_profile_id: Number(profileId), audience_type: audienceType, audience_id: Number(audienceId),
    }),
    onSuccess: () => { toast.success("Profile assigned"); onSaved(); onClose(); },
    onError: (e: any) => toast.error(apiErr(e)),
  });

  const submit = () => {
    if (!profileId) { toast.error("Choose a pricing profile."); return; }
    if (!audienceId) { toast.error(audienceType === "user" ? "Choose a driver." : "Choose a pricing group."); return; }
    mutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Assign Profile</DialogTitle>
          <DialogDescription data-testid="assignment-applicability">
            Rates assigned here apply to these drivers at all chargers in this organization.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Pricing profile</Label>
            <Select value={profileId} onValueChange={setProfileId}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select a pricing profile…" /></SelectTrigger>
              <SelectContent>
                {profiles.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Audience</Label>
              <Select value={audienceType} onValueChange={(v) => { setAudienceType(v as PricingAudienceType); setAudienceId(""); }}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pricing_driver_group">Pricing driver group</SelectItem>
                  <SelectItem value="user">Individual driver</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{audienceType === "user" ? "Driver" : "Group"}</Label>
              {audienceType === "pricing_driver_group" ? (
                <SearchableSelect
                  className="mt-1 w-full"
                  value={audienceId || null}
                  onValueChange={setAudienceId}
                  placeholder="Select group…"
                  searchPlaceholder="Search groups…"
                  emptyText="No groups match."
                  options={groups.map((g) => ({ value: String(g.id), label: g.name }))}
                />
              ) : (
                <SearchableSelect
                  className="mt-1 w-full"
                  value={audienceId || null}
                  onValueChange={setAudienceId}
                  placeholder="Select driver…"
                  searchPlaceholder="Search drivers…"
                  emptyText="No drivers match."
                  options={drivers.map((u) => ({ value: String(u), label: userName.get(u) ?? `#${u}` }))}
                />
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={mutation.isPending}>{mutation.isPending ? "Saving…" : "Assign"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── A pricing driver group card with member management. ── */
function PricingGroupCard({ orgId, group, drivers, userName, onChanged }: {
  orgId: number; group: PricingGroup; drivers: number[];
  userName: Map<number, string>; onChanged: () => void;
}) {
  const qc = useQueryClient();
  const membersQ = useQuery({
    queryKey: ["pricing-group-members", orgId, group.id],
    queryFn: () => fetchPricingGroupMembers(orgId, group.id),
  });
  const members = membersQ.data?.members ?? [];
  const [addUser, setAddUser] = useState("");
  const invalidate = () => qc.invalidateQueries({ queryKey: ["pricing-group-members", orgId, group.id] });

  const addM = useMutation({
    mutationFn: (uid: number) => addPricingGroupMember(orgId, group.id, uid),
    onSuccess: () => { invalidate(); onChanged(); setAddUser(""); },
    onError: (e: any) => toast.error(apiErr(e)),
  });
  const removeM = useMutation({
    mutationFn: (uid: number) => removePricingGroupMember(orgId, group.id, uid),
    onSuccess: () => { invalidate(); onChanged(); },
    onError: (e: any) => toast.error(apiErr(e)),
  });
  const delM = useMutation({
    mutationFn: () => deletePricingGroup(orgId, group.id),
    onSuccess: () => { toast.success("Group deleted"); onChanged(); },
    onError: (e: any) => toast.error(apiErr(e)),
  });

  const candidates = drivers.filter((d) => !members.includes(d));

  return (
    <div className="rounded-lg border p-3" data-testid={`pricing-group-${group.id}`}>
      <div className="flex items-center justify-between">
        <div className="font-medium flex items-center gap-2">
          {group.name}
          {group.is_builtin && <Badge variant="neutral" data-testid="builtin-badge">Built-in · auto-managed</Badge>}
        </div>
        {!group.is_builtin && (
          <Button size="xs" variant="ghost" className="text-destructive"
            data-testid={`delete-group-${group.id}`}
            onClick={() => { if (window.confirm(`Delete pricing group "${group.name}"?`)) delM.mutate(); }}>
            Delete
          </Button>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        {members.length === 0 ? <span className="text-xs text-muted-foreground">No members</span>
          : members.map((m) => (
            <Badge key={m} variant="neutral" className="gap-1">
              {userName.get(m) ?? `#${m}`}
              {!group.is_builtin && (
                <button className="ml-1 text-destructive" data-testid={`remove-member-${group.id}-${m}`}
                  onClick={() => removeM.mutate(m)}>×</button>
              )}
            </Badge>
          ))}
      </div>
      {group.is_builtin ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Membership follows driver enrollment automatically and can’t be edited here.
        </p>
      ) : (
        <div className="mt-2 flex gap-2">
          <Select value={addUser} onValueChange={setAddUser}>
            <SelectTrigger className="h-8 w-48"><SelectValue placeholder="Add a driver…" /></SelectTrigger>
            <SelectContent>
              {candidates.length === 0 ? <div className="px-2 py-1 text-xs text-muted-foreground">All drivers added</div>
                : candidates.map((d) => <SelectItem key={d} value={String(d)}>{userName.get(d) ?? `#${d}`}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="xs" disabled={!addUser} onClick={() => addM.mutate(Number(addUser))}>Add</Button>
        </div>
      )}
    </div>
  );
}

export function PricingProfilesPage() {
  const qc = useQueryClient();
  const [selectedOrg, setSelectedOrg] = useState<number | null>(null);
  const [profileModal, setProfileModal] = useState<{ open: boolean; editing: PricingProfile | null }>({ open: false, editing: null });
  const [assignOpen, setAssignOpen] = useState(false);
  const [newGroup, setNewGroup] = useState("");
  const [enrollUser, setEnrollUser] = useState("");

  const orgsQ = useQuery({ queryKey: ["organizations"], queryFn: fetchOrganizations });
  useEffect(() => {
    if (selectedOrg) return;
    const orgs = orgsQ.data?.organizations ?? [];
    if (orgs.length > 0) setSelectedOrg(orgs[0].id);
  }, [orgsQ.data, selectedOrg]);

  const orgDetailQ = useQuery({ queryKey: ["organization", selectedOrg], queryFn: () => fetchOrganization(selectedOrg!), enabled: !!selectedOrg });
  const profilesQ = useQuery({ queryKey: ["pricing-profiles", selectedOrg], queryFn: () => fetchPricingProfiles(selectedOrg!), enabled: !!selectedOrg });
  const groupsQ = useQuery({ queryKey: ["pricing-groups", selectedOrg], queryFn: () => fetchPricingGroups(selectedOrg!), enabled: !!selectedOrg });
  const driversQ = useQuery({ queryKey: ["pricing-drivers", selectedOrg], queryFn: () => fetchPricingDrivers(selectedOrg!), enabled: !!selectedOrg });
  const assignmentsQ = useQuery({ queryKey: ["pricing-assignments", selectedOrg], queryFn: () => fetchPricingAssignments(selectedOrg!), enabled: !!selectedOrg });

  const profiles = (profilesQ.data?.pricing_profiles ?? []).filter((p) => p.enabled);
  const groups = groupsQ.data?.pricing_groups ?? [];
  const drivers = driversQ.data?.drivers ?? [];
  const assignments = assignmentsQ.data?.assignments ?? [];
  const members = orgDetailQ.data?.organization?.users ?? [];

  const profileNameById = useMemo(() => new Map(profiles.map((p) => [p.id, p.name])), [profiles]);
  const groupNameById = useMemo(() => new Map(groups.map((g) => [g.id, g.name])), [groups]);
  const userName = useMemo(() => new Map(members.map((u) => [u.id, u.username])), [members]);

  const invalidate = (key: string) => qc.invalidateQueries({ queryKey: [key, selectedOrg] });

  const createGroupM = useMutation({
    mutationFn: () => createPricingGroup(selectedOrg!, newGroup.trim()),
    onSuccess: () => { toast.success("Pricing group created"); setNewGroup(""); invalidate("pricing-groups"); },
    onError: (e: any) => toast.error(apiErr(e)),
  });
  const enrollM = useMutation({
    mutationFn: () => enrollPricingDriver(selectedOrg!, Number(enrollUser)),
    onSuccess: () => { toast.success("Driver enrolled"); setEnrollUser(""); invalidate("pricing-drivers"); invalidate("pricing-groups"); },
    onError: (e: any) => toast.error(apiErr(e)),
  });
  const unenrollM = useMutation({
    mutationFn: (uid: number) => unenrollPricingDriver(selectedOrg!, uid),
    onSuccess: () => { toast.success("Driver unenrolled"); invalidate("pricing-drivers"); invalidate("pricing-groups"); },
    onError: (e: any) => toast.error(apiErr(e)),
  });
  const disableProfileM = useMutation({
    mutationFn: (p: PricingProfile) => disablePricingProfile(selectedOrg!, p.id, p.revision),
    onSuccess: () => { toast.success("Pricing profile deleted"); invalidate("pricing-profiles"); },
    onError: (e: any) => { toast.error(apiErr(e)); invalidate("pricing-profiles"); },
  });
  const disableAssignM = useMutation({
    mutationFn: (a: PricingAssignment) => disablePricingAssignment(selectedOrg!, a.id, a.revision),
    onSuccess: () => { toast.success("Assignment removed"); invalidate("pricing-assignments"); },
    onError: (e: any) => { toast.error(apiErr(e)); invalidate("pricing-assignments"); },
  });

  const nonDriverMembers = members.filter((u) => !drivers.includes(u.id));

  return (
    <div className="space-y-5 pt-[86px]">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <DollarSign className="h-6 w-6" /> Pricing Profiles
          </h1>
          <p className="text-sm text-muted-foreground" data-testid="org-wide-copy">
            Rates assigned here apply to these drivers at all chargers in this organization.
          </p>
        </div>
        {(orgsQ.data?.organizations ?? []).length > 1 && (
          <div data-testid="org-selector">
            <OrgSelector orgs={orgsQ.data?.organizations ?? []} selected={selectedOrg} onSelect={setSelectedOrg} />
          </div>
        )}
      </div>

      {/* A. Pricing Profiles catalog */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Pricing Profiles</CardTitle>
          <Button size="sm" disabled={!selectedOrg} onClick={() => setProfileModal({ open: true, editing: null })}>
            <Plus className="h-4 w-4 mr-1" /> Create Pricing Profile
          </Button>
        </CardHeader>
        <CardContent>
          {profiles.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center" data-testid="profiles-empty">
              Create a pricing profile and assign it to drivers or a pricing group.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-2 font-medium">Name</th><th className="py-2 font-medium">Type</th>
                  <th className="py-2 font-medium">Pricing</th><th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {profiles.map((p) => (
                  <tr key={p.id} className="border-b last:border-0" data-testid={`profile-row-${p.id}`}>
                    <td className="py-2 font-medium">{p.name}</td>
                    <td className="py-2"><Badge variant={isPricebookFree(p.pricebook) ? "success" : "neutral"}>{isPricebookFree(p.pricebook) ? "Free" : "Paid"}</Badge></td>
                    <td className="py-2" data-testid={`profile-summary-${p.id}`}>{summarizePricebook(p.pricebook)}</td>
                    <td className="py-2 text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="xs" variant="outline" onClick={() => setProfileModal({ open: true, editing: p })}>Edit</Button>
                        <Button size="xs" variant="ghost" className="text-destructive"
                          onClick={() => { if (window.confirm(`Delete "${p.name}"?`)) disableProfileM.mutate(p); }}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* B. Drivers (enrollment) */}
      <Card>
        <CardHeader><CardTitle>Drivers</CardTitle></CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground mb-3">
            Drivers enrolled with this organization. Enrolled drivers are automatically in
            <b> All Drivers</b>. An organization admin only receives driver pricing once enrolled here.
          </p>
          <div className="flex flex-wrap gap-1 mb-3" data-testid="drivers-list">
            {drivers.length === 0 ? <span className="text-xs text-muted-foreground">No drivers enrolled</span>
              : drivers.map((d) => (
                <Badge key={d} variant="neutral" className="gap-1" data-testid={`driver-${d}`}>
                  {userName.get(d) ?? `#${d}`}
                  <button className="ml-1 text-destructive" data-testid={`unenroll-${d}`} onClick={() => unenrollM.mutate(d)}>×</button>
                </Badge>
              ))}
          </div>
          <div className="flex gap-2">
            <Select value={enrollUser} onValueChange={setEnrollUser}>
              <SelectTrigger className="h-8 w-56"><SelectValue placeholder="Enroll an org member as driver…" /></SelectTrigger>
              <SelectContent>
                {nonDriverMembers.length === 0 ? <div className="px-2 py-1 text-xs text-muted-foreground">All members enrolled</div>
                  : nonDriverMembers.map((u) => <SelectItem key={u.id} value={String(u.id)}>{u.username}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="xs" disabled={!enrollUser} data-testid="enroll-driver" onClick={() => enrollM.mutate()}>Enroll</Button>
          </div>
        </CardContent>
      </Card>

      {/* C. Pricing driver groups */}
      <Card>
        <CardHeader><CardTitle>Pricing Driver Groups</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {groups.map((g) => (
            <PricingGroupCard key={g.id} orgId={selectedOrg!} group={g} drivers={drivers}
              userName={userName} onChanged={() => { invalidate("pricing-groups"); }} />
          ))}
          <div className="flex gap-2">
            <Input className="h-8 w-56" placeholder="New pricing group name…" value={newGroup}
              data-testid="new-group-name" onChange={(e) => setNewGroup(e.target.value)} />
            <Button size="xs" disabled={!newGroup.trim() || !selectedOrg} data-testid="create-group"
              onClick={() => createGroupM.mutate()}>Create group</Button>
          </div>
        </CardContent>
      </Card>

      {/* D. Assignments */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Assignments</CardTitle>
          <Button size="sm" disabled={!selectedOrg || profiles.length === 0} onClick={() => setAssignOpen(true)}>
            <Plus className="h-4 w-4 mr-1" /> Assign Profile
          </Button>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground mb-3" data-testid="fallback-note">
            Rates assigned here apply to these drivers at all chargers in this organization. Drivers without an
            assignment, and guests, use the charger’s default rate.
          </p>
          {assignments.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center" data-testid="assignments-empty">
              No assignments yet. Assign a pricing profile to a driver or a pricing group.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-2 font-medium">Pricing profile</th>
                  <th className="py-2 font-medium">Audience</th><th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((a) => (
                  <tr key={a.id} className="border-b last:border-0" data-testid={`assignment-row-${a.id}`}>
                    <td className="py-2 font-medium">{a.profile_name ?? profileNameById.get(a.pricing_profile_id) ?? `#${a.pricing_profile_id}`}</td>
                    <td className="py-2">
                      {a.audience_type === "pricing_driver_group"
                        ? <>Pricing group · {groupNameById.get(a.audience_id) ?? `#${a.audience_id}`}</>
                        : <>Driver · {userName.get(a.audience_id) ?? `#${a.audience_id}`}</>}
                    </td>
                    <td className="py-2 text-right">
                      <Button size="xs" variant="ghost" className="text-destructive"
                        onClick={() => { if (window.confirm("Remove this assignment?")) disableAssignM.mutate(a); }}>Remove</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {selectedOrg != null && (
        <>
          <ProfileFormModal open={profileModal.open} editing={profileModal.editing} orgId={selectedOrg}
            onClose={() => setProfileModal({ open: false, editing: null })} onSaved={() => invalidate("pricing-profiles")} />
          <AssignmentFormModal open={assignOpen} orgId={selectedOrg} profiles={profiles} groups={groups}
            drivers={drivers} userName={userName}
            onClose={() => setAssignOpen(false)} onSaved={() => invalidate("pricing-assignments")} />
        </>
      )}
    </div>
  );
}
